import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// routes
import { PATH_DASHBOARD } from 'src/routes/paths';
// components
import ChatBottomBar from 'src/sections/@chats/chat/ChatBottomBar';
import ChatHeader from 'src/sections/@chats/chat/ChatHeader';
import ChatMessageList from './ChatMessageList';
import ChatInfoSidebar from 'src/sections/@chats/chat/ChatInfoSidebar';
// hooks
import useAuth from 'src/hooks/useAuth';
// redux
import { useDispatch, useSelector } from 'src/redux/store';
// actions
import DrawerChatGpt from 'src/components/_chats/chatgpt/DrawerChatgpt';
import v1_actionEnviarGpt from 'src/actions/v1/chatgpt/v1_actionEnviarGpt';
// helpers
import { getMessagesBySubstring } from 'src/helpers/cache.helper';
import { getSentimentAnalysis } from 'src/redux/slices/chat';
import { verifyRules } from 'src/utils/functions';

// ----------------------------------------------------------------------

export default function ChatWindow({
    config,
    chatInfo,
    sidebarLeft,
    changeOpenSidebarLeft,
    source,
    onCloseModal,
    layoutMode,
    hideSidebar = false,
    onSidebarToggle,
}) {
    const { pathname } = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslation('chat');
    const {
        loadingChat,
        mensagens,
        chatIsOnline,
        mensagensEnviar,
        mensagensArquivos,
        chatsAnalysis,
    } = useSelector((state) => state.chat);
    const { isUserStandby } = useSelector((state) => state.user);
    const { onlineUsers } = useSelector((state) => state.staff);
    const { listaUsuariosTransferencia } = useSelector((state) => state.user);
    const { user, cliente } = useAuth();

    const [messagesError, setMessagesError] = useState([]);
    const [drawerChatgpt, setDrawerChatgpt] = useState(false);
    const [respostaGpt, setRespostaGpt] = useState('');
    const [respostasGpt, setRespostasGpt] = useState([]);
    const [loadingGpt, setLoadingGpt] = useState(false);
    const [conversacao, setConversacao] = useState([]);
    const [textInputDrawer, setTextInputDrawer] = useState('');
    const [isShowContextOptions, setIsShowContextOptions] = useState(false);
    const [openSidebar, setOpenSidebar] = useState(true);
    const [showAlert, setShowAlert] = useState(false);

    // Constants
    const CHAT_STATUS = {
        ATTENDING: 'Atendendo',
        FINISHED: 'Finalizado',
    };

    // Memoized values
    const messagesMerge = useMemo(() => {
        return mensagens.concat(mensagensEnviar).concat(messagesError);
    }, [mensagens, mensagensEnviar, messagesError]);

    const messagesMergeArquivos = useMemo(() => {
        return mensagensArquivos.concat(mensagensEnviar).reverse();
    }, [mensagensArquivos, mensagensEnviar]);

    // Functions
    async function getMessagesError() {
        const messages_error = await getMessagesBySubstring(`${chatInfo._id}_`);
        setMessagesError(messages_error);
    }

    const handleEnviarGpt = async (mensagens) => {
        setDrawerChatgpt(true);
        setIsShowContextOptions(false);
        const arrayFilter = mensagens.filter((i) => i.type === 'chat');
        const updatedArr = arrayFilter.map((item) => {
            return {
                body: item.body.replace(/\*\_.*?\_\*/g, ''),
                type: item.type,
                fromMe: item.fromMe,
            };
        });

        setLoadingGpt(true);
        try {
            const querys = `?usuario_id=${user.id}&cliente_id=${user.clienteId}`;
            const response_chatgpt = await v1_actionEnviarGpt(updatedArr, querys);
            if (response_chatgpt.data.data.length > 0) {
                const resposta = response_chatgpt.data.data[0].message.content;
                const newObj = {
                    body: resposta,
                    type: 'chat',
                    fromMe: false,
                    fromGpt: true,
                };
                setConversacao((prev) => [...prev, newObj]);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingGpt(false);
        }
    };

    const onStartConversationGpt = async (inputText, isGatilho, nomeGatilho) => {
        try {
            const newObj = { body: inputText, type: 'chat', fromMe: true };
            const newObjPush = { body: nomeGatilho, type: 'chat', fromMe: true };
            setConversacao((prev) => [...prev, newObjPush]);
            setLoadingGpt(true);
            const querys = `?usuario_id=${user.id}&cliente_id=${user.clienteId}&ignore_instruction=1`;
            const response_chatgpt = await v1_actionEnviarGpt([newObj], querys);
            if (response_chatgpt.data.data.length > 0) {
                const resposta = response_chatgpt.data.data[0].message.content;
                const newObjResponse = {
                    body: resposta,
                    type: 'chat',
                    fromMe: false,
                    fromGpt: true,
                };
                setConversacao((prev) => [...prev, newObjResponse]);
                setTextInputDrawer('');
            }
        } catch (error) {
            console.error('onStartConversationGpt error', error);
        } finally {
            setLoadingGpt(false);
        }
    };

    const onStartConversationGptFree = async () => {
        try {
            const newObj = { body: textInputDrawer, type: 'chat', fromMe: true };
            setConversacao((prev) => [...prev, newObj]);
            setLoadingGpt(true);
            const querys = `?usuario_id=${user.id}&cliente_id=${user.clienteId}&ignore_instruction=1`;
            const response_chatgpt = await v1_actionEnviarGpt([newObj], querys);
            if (response_chatgpt.data.data.length > 0) {
                const resposta = response_chatgpt.data.data[0].message.content;
                const newObjResponse = {
                    body: resposta,
                    type: 'chat',
                    fromMe: false,
                    fromGpt: true,
                };
                setConversacao((prev) => [...prev, newObjResponse]);
                setTextInputDrawer('');
            }
        } catch (error) {
            console.error('onStartConversationGptFree error', error);
        } finally {
            setLoadingGpt(false);
        }
    };

    const handleCallbackGpt = async (value) => {
        if (!value) return;
        setDrawerChatgpt(true);
        onStartConversationGpt(
            ('Texto:' + value.message + '. Instrução:' + value.input).replace(/\*\_.*?\_\*|\n/g, ''),
            true,
            value.nome
        );
    };

    // Effects
    useEffect(() => {
        getMessagesError();
    }, [chatInfo, mensagensEnviar]);

    useEffect(() => {
        if (verifyRules(user?.rules || [], 'chats.show_bottomBar') && chatInfo.statusRoom !== CHAT_STATUS.ATTENDING) {
            setShowAlert(true);
        } else {
            setShowAlert(false);
        }
    }, [chatInfo?.statusRoom, user?.rules]);

    useEffect(() => {
        if (source === 'funil.chat') {
            setOpenSidebar(false);
        }
    }, [source]);

    useEffect(() => {
        dispatch(getSentimentAnalysis(chatInfo?._id, user.clienteId));
    }, [chatInfo?._id, dispatch, user.clienteId]);

    const heightClass =
        layoutMode === 'sidebar'
            ? 'h-full'
            : source === 'funil.chat'
                ? 'h-[calc(100vh-150px)] sm:h-[calc(90vh-150px)] lg:h-[calc(90vh-130px)]'
                : source === 'chat.supervisionar'
                    ? 'h-[calc(100vh-185px)]'
                    : 'h-[calc(100vh-125px)]';

    return (
        <div className="flex w-full flex-grow">
            {drawerChatgpt && (
                <DrawerChatGpt
                    open={drawerChatgpt}
                    user={user}
                    onClear={() => {
                        setRespostaGpt('');
                        setRespostasGpt([]);
                        setConversacao([]);
                        setDrawerChatgpt(false);
                    }}
                    loadingGpt={loadingGpt}
                    conversacao={conversacao}
                    mensagens={mensagens}
                    onClose={() => setDrawerChatgpt(false)}
                    resposta={respostaGpt}
                    respostas={respostasGpt}
                    onEnviarGpt={() => handleEnviarGpt(mensagens)}
                    onGerarResposta={handleEnviarGpt}
                    onStartConversationGpt={() => onStartConversationGptFree()}
                    isShowContextOptions={isShowContextOptions}
                    textInputDrawer={textInputDrawer}
                    changeText={(e) => setTextInputDrawer(e.target.value)}
                    choice={(value) => {
                        // callbackSendTextBottomBar(value.body) // Needs reference
                        setDrawerChatgpt(false);
                        setRespostaGpt('');
                    }}
                />
            )}

            <div className="flex flex-row w-full h-full">
                <div className="w-full">
                    {!isUserStandby && (
                        <ChatHeader
                            openSidebar={hideSidebar ? false : openSidebar}
                            sidebarLeft={sidebarLeft}
                            changeOpenSidebarLeft={() => {
                                if (hideSidebar && onSidebarToggle) {
                                    onSidebarToggle();
                                } else {
                                    setOpenSidebar(false);
                                }
                            }}
                            changeOpenSidebar={() => {
                                changeOpenSidebarLeft();
                                if (hideSidebar && onSidebarToggle) {
                                    onSidebarToggle();
                                } else {
                                    setOpenSidebar(!openSidebar);
                                }
                            }}
                            cliente={cliente}
                            user={user}
                            chatIsOnline={chatIsOnline}
                            onlineUsers={onlineUsers}
                            chat={chatInfo}
                            listaUsuariosTransferencia={listaUsuariosTransferencia}
                            source={source}
                            onCloseModal={onCloseModal}
                            mensagens={messagesMerge}
                        />
                    )}

                    <div
                        className={`flex flex-col flex-1 overflow-hidden dark:bg-none bg-gradient-to-br from-[#e2e8f0] to-[#ffffff] ${heightClass}`}
                    >
                        {chatInfo.canal !== 'instagram' && (
                            <ChatMessageList
                                source={source}
                                callbackGpt={handleCallbackGpt}
                                config={config}
                                user={user}
                                loadingChat={loadingChat}
                                cliente={cliente}
                                chat={chatInfo}
                                conversa={messagesMerge}
                                mensagens={mensagens}
                                onCallbackResend={getMessagesError}
                                sentimentAnalysis={chatsAnalysis}
                            />
                        )}

                        <div>
                            {!isUserStandby &&
                                (chatInfo.statusRoom === CHAT_STATUS.ATTENDING || verifyRules(user?.rules || [], 'chats.show_bottomBar')) && (
                                    <>
                                        {/* Alerta estático para chat não em atendimento */}
                                        {showAlert && (
                                            <div className="flex items-center justify-between mx-4 my-2 px-4 py-3 rounded bg-[#fff3cd] border border-[#ffeaa7]">
                                                <div className="flex items-center">
                                                    <div className="w-2 h-2 rounded-full bg-[#f39c12] mr-3" />
                                                    <span className="font-medium text-[#856404]">{t('header.notInServiceWarning')}</span>
                                                </div>
                                                <div onClick={() => setShowAlert(false)} className="cursor-pointer text-lg font-bold text-[#856404] hover:opacity-70">
                                                    ×
                                                </div>
                                            </div>
                                        )}

                                        <ChatBottomBar
                                            layoutMode={layoutMode}
                                            cliente={cliente}
                                            onOpenDrawerGpt={() => {
                                                setIsShowContextOptions(true);
                                                setDrawerChatgpt(true);
                                            }}
                                            user={user}
                                            mensagens={messagesMerge}
                                            disabled={pathname === PATH_DASHBOARD.chat.new}
                                            chat={chatInfo}
                                            onCallbackResend={getMessagesError}
                                            onCallbackAnalysis={() => dispatch(getSentimentAnalysis(chatInfo?._id))}
                                        />
                                    </>
                                )}
                        </div>
                    </div>
                </div>

                {!hideSidebar && openSidebar && (
                    <div
                        className={`overflow-visible bg-background w-[320px] min-w-[320px]`}
                        style={{ width: openSidebar ? '320px' : '0', minWidth: openSidebar ? '320px' : '0' }}
                    >
                        <ChatInfoSidebar source={source} chat={chatInfo} messagesWpp={messagesMergeArquivos} />
                    </div>
                )}
            </div>
        </div>
    );
}
